import { FC } from 'react';
import classNames from 'classnames';

import { TreezStore } from '@/components';
import { TreezAndExternalStore } from '@/utils';

import { useStoreLocatorContext } from './context';
import { StoreExternalItem } from './StoreLocator.components';
import styles from './storelocator.module.scss';

interface IStoresListProps {
  loadingLocation: boolean;
  stores: TreezAndExternalStore[];
  panelId: string;
  searchTerm?: string;
  showEmptyTabMessage?: boolean;
  ariaLabelledByTabId?: string;
  emptyNoStoresLabel?: string;
  onSelectedStoreId?: (_id?: string) => void;
  hideCurbside?: boolean;
}

const StoresList: FC<IStoresListProps> = ({
  loadingLocation,
  stores,
  panelId,
  searchTerm = '',
  showEmptyTabMessage,
  ariaLabelledByTabId,
  emptyNoStoresLabel = 'No stores available',
  onSelectedStoreId,
  hideCurbside,
}) => {
  const { selectedStoreId, setSelectedStoreId } = useStoreLocatorContext();
  const onSelected = onSelectedStoreId ?? setSelectedStoreId;

  const emptySearchList = () => {
    return (
      <div className={styles.empty_locations} role="alert">
        <i
          className={classNames(
            'icon icon_search',
            styles.empty_locations__icon_search,
          )}
          role="presentation"
        />
        <span>{emptyNoStoresLabel}</span>
        <p>
          No search results match
          <b>
            <em>&quot;{searchTerm}&quot;</em>
          </b>{' '}
          Try a different search
        </p>
      </div>
    );
  };

  const emptyTabFilter = () => (
    <div className={styles.empty_locations} role="status">
      <span>No locations available</span>
      <p>There are no shops in this region.</p>
    </div>
  );

  const hasSearch = !!searchTerm.trim();

  return (
    <fieldset
      aria-labelledby={ariaLabelledByTabId}
      id={panelId}
      role={ariaLabelledByTabId ? 'tabpanel' : undefined}
    >
      {showEmptyTabMessage && emptyTabFilter()}

      {!stores?.length &&
        !showEmptyTabMessage &&
        (hasSearch ? (
          emptySearchList()
        ) : (
          <div className={styles.empty_locations} role="alert">
            <span>{emptyNoStoresLabel}</span>
          </div>
        ))}

      {stores?.map(store => {
        const isActive = selectedStoreId === store.short_name;

        if (store?.externalStore) {
          return (
            <StoreExternalItem
              isOpen={true}
              isSelected={selectedStoreId === store?.short_name}
              key={store?.short_name}
              onSelected={onSelected}
              store={store}
            />
          );
        }

        return (
          <TreezStore
            className={styles.store}
            distanceInMiles={store?.distance ?? ''}
            hideCurbside={hideCurbside}
            isActive={isActive}
            isLoadingLocation={loadingLocation}
            isRadioType
            key={store?.short_name}
            onSelected={onSelected}
            store={store}
            withBorders={false}
          />
        );
      })}
    </fieldset>
  );
};

export { StoresList };
